<template>
  <div class="advance-reporting-chart-management-popup">
    <ErrorHandler
      v-if="state.error != null"
      :error="state.error"
      @done="state.error = null"
    />
    <template v-if="state.loading">
      <div class="d-flex justify-content-center">
        <CSpinner color="primary" />
      </div>
    </template>
    <template v-else>
      <div class="popup-content">
        <div v-if="chartList.length > 0" class="section">
          <div class="advance-reporting-select-all-container">
            <div class="advance-reporting-checkbox-container">
              <input
                type="checkbox"
                :checked="isAllChartsSelected"
                @change="toggleAllCharts"
                @mousedown.stop
                @click.stop
                class="checkbox-input"
              />
              <div
                class="checkbox-visual"
                :class="{ checked: isAllChartsSelected }"
                @mousedown.stop
                @click.stop="toggleAllCharts"
              >
                <i class="ri-check-line" v-if="isAllChartsSelected"></i>
              </div>
            </div>
            <span class="select-all-label">Select All</span>
          </div>
          <div class="chart-list">
            <draggable
              v-model="chartList"
              :animation="200"
              ghost-class="ghost"
              chosen-class="chosen"
              drag-class="drag"
              @end="handleDragEnd"
              item-key="id"
              :force-fallback="true"
              :fallback-tolerance="5"
              handle=".chart-data-point__drag-handle"
            >
              <template #item="{ element: chart }">
                <ChartDataPointComponent
                  :chart-id="chart.id"
                  :chart-name="chart.name"
                  :is-selected="Boolean(chart.status)"
                  :show-drag-handle="true"
                  :checkbox-id="chart.id"
                  @toggle="(chartId) => toggleChart(chartId)"
                />
              </template>
            </draggable>
          </div>
        </div>
      </div>

      <div v-if="chartList.length > 0" class="popup-footer">
        <BaseButton rounded @click="handleCancel">Cancel</BaseButton>
        <BaseButton rounded @click="handleRestoreToDefault">
          Restore Default
        </BaseButton>
        <LoadingButton
          :is-loading="state.saving"
          rounded
          solid
          @click="handleSave"
        >
          {{ state.saving ? "Saving..." : "Save" }}
        </LoadingButton>
      </div>
    </template>
  </div>
</template>

<script>
import advanceReportingConstants from "@/constants/advanceReportingConstants"
import ChartDataPointComponent from "@/v3components/AdvanceReporting/ChartDataPointComponent.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import ErrorHandler from "@/v3components/VisitorErrorHandler.vue"
import { computed, onMounted, reactive } from "vue"
import draggable from "vuedraggable"
import { useStore } from "vuex"

export default {
  name: "ChartManagementPopUp",
  components: {
    BaseButton,
    LoadingButton,
    ChartDataPointComponent,
    draggable,
    ErrorHandler
  },
  emits: ["save", "cancel"],
  setup(_props, { emit }) {
    const store = useStore()
    const state = reactive({
      chartList: [],
      saving: false,
      loading: false,
      error: null
    })

    const configurableChartList = computed(() => {
      return store.getters["advanceReportingStore/configurableChartList"]
    })
    const chartList = computed({
      get() {
        return state.chartList
      },
      set(value) {
        state.chartList = value
      }
    })
    const isAllChartsSelected = computed(() => {
      if (state.chartList.length === 0) return false
      return state.chartList.every((chart) => chart.status)
    })

    const toggleChart = (beChartId) => {
      const chartIndex = state.chartList.findIndex((chart) => {
        return chart.id === beChartId
      })
      if (chartIndex !== -1) {
        state.chartList[chartIndex].status = Boolean(
          !state.chartList[chartIndex].status
        )
      }
    }

    const toggleAllCharts = () => {
      const shouldSelectAll = !isAllChartsSelected.value
      state.chartList.forEach((chart) => {
        chart.status = shouldSelectAll
      })
    }

    const handleDragEnd = () => {}

    const handleSave = () => {
      return updateChartSettings()
    }

    const handleRestoreToDefault = () => {
      state.chartList = structuredClone([...configurableChartList.value])
    }

    const handleCancel = () => {
      emit("cancel")
    }

    const getChartSettings = async (skipLoading = false) => {
      try {
        if (!skipLoading) {
          state.loading = true
        }
        const response = await store.dispatch(
          "advanceReportingStore/fetchChartSettings"
        )
        const charts = response?.data?.chartSettings || []
        if (charts.length === 0) {
          state.chartList = structuredClone(configurableChartList.value)
        } else {
          state.chartList = structuredClone(
            charts.filter((chart) => {
              return advanceReportingConstants.CONFIGURABLE_CHART_IDS.includes(
                chart.feChartId
              )
            })
          )
        }
      } catch (error) {
        state.error = error
      } finally {
        if (!skipLoading) {
          state.loading = false
        }
      }
    }

    const updateChartSettings = async () => {
      try {
        state.saving = true
        const unconfigurableChartSettings = [
          {
            chart_id:
              store?.getters?.[
                "advanceReportingStore/frontendIdVsBackendIdMap"
              ]?.[advanceReportingConstants.DEFAULT_CHART_IDS.USERS_KPI] ||
              null,
            index: 1,
            status: 1
          },
          {
            chart_id:
              store?.getters?.[
                "advanceReportingStore/frontendIdVsBackendIdMap"
              ]?.[
                advanceReportingConstants.DEFAULT_CHART_IDS
                  .SCHOOL_YEAR_PASS_BREAKDOWN
              ] || null,
            index: 2,
            status: 1
          },
          {
            chart_id:
              store?.getters?.[
                "advanceReportingStore/frontendIdVsBackendIdMap"
              ]?.[
                advanceReportingConstants.DEFAULT_CHART_IDS
                  .SCHOOL_YEAR_PASS_TREND
              ] || null,
            index: 3,
            status: 1
          }
        ]
        const configurableChartSettings = state.chartList.map(
          (chart, chartIndex) => {
            return {
              chart_id: chart.id,
              index: chartIndex + 4,
              status: chart.status ? 1 : 0
            }
          }
        )
        const charts = [
          ...unconfigurableChartSettings,
          ...configurableChartSettings
        ]
        const params = {
          charts
        }
        await store.dispatch(
          "advanceReportingStore/updateChartSettings",
          params
        )
        await getChartSettings(true)
        emit("save")
        emit("cancel")
      } catch (error) {
        state.error = error
      } finally {
        state.saving = false
      }
    }
    onMounted(async () => {
      await getChartSettings()
    })

    return {
      state,
      chartList,
      toggleChart,
      isAllChartsSelected,
      toggleAllCharts,
      handleDragEnd,
      handleSave,
      handleRestoreToDefault,
      handleCancel
    }
  }
}
</script>
